import {
	type CompanyBankDetailWithRelationsResponseDto,
	getApiUrl,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { MarketPaths } from "Market/Routes/Routing";
import { Box, Chip, Grid2, Stack, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useNavigate } from "react-router";
import { BankDetailEditModal } from "./BankDetailEditModal";

type Props = {
	bankDetail: CompanyBankDetailWithRelationsResponseDto;
	companyId: number;
};

const bankDetailResources = Resources.base.bankDetail.form;

export const BankDetailEditCard: FC<Props> = ({ bankDetail, companyId }) => {
	const { t } = useResource();
	const [editModalOpen, setEditModalOpen] = useState(false);
	const navigate = useNavigate();

	const handleNavigateToMarket = (marketPath: string) => {
		navigate(MarketPaths.EditGeneral.path(marketPath));
	};

	return (
		<>
			<Stack
				border={1}
				padding={2}
				borderRadius={1}
				gap={3}
				height="100%"
				sx={{
					cursor: "pointer",
					borderColor: (theme) => theme.palette.divider,
				}}
				onClick={() => setEditModalOpen(true)}
			>
				<Stack flexDirection="row" alignItems="center" gap={1}>
					<Box
						sx={{
							width: 80,
							height: 40,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							overflow: "hidden",
						}}
					>
						<img
							src={`${getApiUrl()}${bankDetail.logoUrlPath}`}
							alt="Bank logo"
							style={{
								width: "100%",
								height: "100%",
								objectFit: "contain",
							}}
						/>
					</Box>
					<Typography variant="h5">{bankDetail.name}</Typography>
				</Stack>
				<Grid2 container spacing={1}>
					<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
						<Typography variant="subtitle1" color="secondary">
							{t(bankDetailResources.bankName)}
						</Typography>
						<Typography variant="caption">{bankDetail.bankName}</Typography>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
						<Typography variant="subtitle1" color="secondary">
							{t(bankDetailResources.bankAccount)}
						</Typography>
						<Typography variant="caption">{bankDetail.bankAccount}</Typography>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
						<Typography variant="subtitle1" color="secondary">
							{t(bankDetailResources.bankCode)}
						</Typography>
						<Typography variant="caption">{bankDetail.bankCode}</Typography>
					</Grid2>
					<Grid2 size={{ xs: 12 }}>
						<Typography variant="subtitle1" color="secondary">
							{t(bankDetailResources.iban)}
						</Typography>
						<Typography variant="caption">{bankDetail.iban}</Typography>
					</Grid2>
				</Grid2>

				<Stack flexDirection="row" flexWrap="wrap" gap={1}>
					{bankDetail.markets.map((market) => (
						<Chip
							key={market.id}
							avatar={
								market.iconPath ? (
									<img
										src={`${getApiUrl()}${market.iconPath}`}
										alt="Market flag"
										style={{
											width: "20px",
											height: "20px",
										}}
									/>
								) : (
									<></>
								)
							}
							label={market.name}
							sx={{
								display: "flex",
								maxWidth: "140px",
								justifyContent: "space-between",
								alignItems: "center",
							}}
							onClick={() => handleNavigateToMarket(market.urlPath)}
						/>
					))}
				</Stack>
			</Stack>
			{editModalOpen && (
				<BankDetailEditModal
					isOpen={editModalOpen}
					setIsOpen={setEditModalOpen}
					companyId={companyId}
					bankDetail={bankDetail}
				/>
			)}
		</>
	);
};
